import React, { useState, useEffect, useRef } from "react";
import { RegiftCardContext } from "../../pages/RegiftCardPage";
import { AppSettingContext } from "../Application";
import { IRegiftCardStep, RegiftCardStepModel } from "../../models/RegiftCardStep.model";
import { RegiftCardStateEnum } from "../../models/enums/RegiftCardStateEnum";
import { IBaseStepProps } from "../../models/props/BaseStepProps.interface";
import LoadingComponent from "../shared/LoadingComponent";
import { CheckBalanceStepModel } from "../../models/CheckBalanceStep.model";

interface IRegiftCardStepProps extends IBaseStepProps {
    isSaving: boolean;
    handleSubmit(model: IRegiftCardStep): void;
}

const RegiftCardStep = (props: IRegiftCardStepProps) => {
    //global context
    const { currentState, checkBalanceStep, regiftCardStep } = React.useContext(RegiftCardContext);
    const { debugMode, labels, links } = React.useContext(AppSettingContext);
    const IS_ACTIVE = (checkBalanceStep && CheckBalanceStepModel.validateModel(checkBalanceStep) && !checkBalanceStep.isDirty);

    const [culture, setCulture] = useState('en-GB');
    const [pageModel, setPageModel] = useState({value:""});

    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);

    // internal refs
    const checkboxRef = useRef(null)

    useEffect(() => {
        checkboxRef.current.checked = currentState == RegiftCardStateEnum.REGIFT_CARD || IS_ACTIVE;
    });

    useEffect(() => {
        setIsLoading(true);
        setCulture(document.documentElement.lang);

        const modelCopy = Object.assign({}, pageModel);
        modelCopy.value = regiftCardStep.value > 0 ? regiftCardStep.value.toString() : "";
        setPageModel(modelCopy);

    }, []);

    useEffect(() => {

        setIsLoading(false);

        props.handleDirtyState(isModelDirty());

    }, [pageModel]);

    const handleExpandBoxCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (checkboxRef.current.checked) {
            props.handleBeginEdit();
        }
    };

    const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        console.debug("handleCardCodeChange:", e.target.value);
        if (e.target.value == "") {
            setPageModel({ ...pageModel, value: e.target.value });
        }
        else {
            const value = Math.max(1, Math.min(checkBalanceStep.remainingBalance, Number(e.target.value)));
            setPageModel({ ...pageModel, value: value.toString() });
        }
    };

    const handleSubmit = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();

        setIsSaving(false);
        props.handleSubmit({
            value: parseInt(pageModel.value),
            isDirty: false
        });

        checkboxRef.current.checked = false; //collapse the step
    };

    const isValid = (): boolean => {
        const model = getRegiftCardStepModel();
        return RegiftCardStepModel.validateModel(model, checkBalanceStep.remainingBalance);
    };

    const isModelDirty = (): boolean => {
        if (!pageModel) return false;

        const model = getRegiftCardStepModel();
        if (model.value != regiftCardStep.value) {
            return true;
        }

        return false;
    };

    const getRegiftCardStepModel = (): IRegiftCardStep => {
        const model: IRegiftCardStep = {
            value: parseInt(pageModel.value),
            isDirty: true
        };
        return model;
    };

    const getFormatedText = (): string => {
        let balance = labels.get("GiftCard.App.Steps.RegiftCard.Description");
        balance = balance.replace("{0}", `${checkBalanceStep.currencySymbol}${checkBalanceStep.remainingBalance.toFixed(2)}`);
        return balance;
    };

    if (error) throw error;
    return (
        <div className="regift-card box">
            <input
                ref={checkboxRef}
                onChange={handleExpandBoxCheck}
                defaultChecked={false} 
                type="checkbox" name="regift-card-box-hide" id="regift-card-box-hide" data-regift-card-box-hide/>

            <div className={`action-card ${IS_ACTIVE === false && "inactive"}`}>
                <h3>{labels.get("GiftCard.App.Steps.RegiftCard")}</h3>

                <div className="edit">
                    {IS_ACTIVE &&
                        <label htmlFor="regift-card-box-hide" className="label-fs">
                            <i></i>
                        </label>}
                </div>
                <div className="show">
                    {(isLoading || isSaving) && <LoadingComponent />}

                    {(!isLoading && !isSaving && pageModel) &&
                        <div className="slide-content">
                            {(debugMode) &&
                                <div>
                                    <p>Value: {pageModel.value}</p>
                                    <p>Balance: {checkBalanceStep.remainingBalance}</p>
                                    <p>Currency: {checkBalanceStep.currencySymbol}</p>
                                    <br />
                                    <p>Is dirty: {String(isModelDirty())}</p>
                                    <p>Is valid: {String(isValid())}</p>
                                </div>}

                            <label htmlFor="balance__options_card_code" className="label-fs">{labels.get("GiftCard.App.Steps.RegiftCard.Input.Title")}</label>
                            <input type="number"
                                value={pageModel.value}
                                onInput={handleValueChange}
                                name="balance__options_card_code" id="balance__options_card_code"
                                placeholder={labels.get("GiftCard.App.Steps.RegiftCard.Input.Placeholder")} />

                            <small className="small-fs">{getFormatedText()}</small>
                            <button className="btn btn__full_width btn__primary large-fs btn__large"
                                disabled={(!isValid())}
                                onClick={handleSubmit}>{labels.get("GiftCard.App.Steps.RegiftCard.Btn.Save")}</button>
                        </div>}
                </div>
            </div>
        </div>
    )
}

export default RegiftCardStep;